import { Alert, Badge, Group, Text } from '@mantine/core';
import type { ConnectionStatus } from '../connection.js';
import type { ClientError } from '../store.js';

/**
 * 接続状態とエラーの表示。
 *
 * 切断は例外ではなく通常運転なので、赤い警告では出さない。再接続中であることを
 * 静かに示す（design 7.1）。
 */

const STATUS_LABEL: Record<ConnectionStatus, string> = {
  connecting: '接続中',
  open: '接続',
  reconnecting: '再接続中',
  closed: '切断',
  // Badge に収める必要があるので短く。詳細は RejectedNotice / ClosedNotice が出す。
  rejected: 'ルーム不明',
  roomClosed: 'ルーム削除',
};

export function ConnectionBadge({ status }: { status: ConnectionStatus }): React.ReactElement {
  const color =
    status === 'open'
      ? 'teal'
      : status === 'reconnecting' || status === 'connecting'
        ? 'yellow'
        : 'red';
  return (
    <Badge color={color} variant="light">
      {STATUS_LABEL[status]}
    </Badge>
  );
}

export function ErrorBanner({
  error,
  onClose,
}: {
  error: ClientError | null;
  onClose: () => void;
}): React.ReactElement | null {
  if (error === null) return null;
  return (
    <Alert
      color="red"
      withCloseButton
      onClose={onClose}
      title="操作できませんでした"
      data-testid="error-banner"
      data-code={error.code}
    >
      <Group gap="xs">
        <Text size="sm">{error.message}</Text>
        <Text size="xs" c="dimmed">
          ({error.code})
        </Text>
      </Group>
    </Alert>
  );
}

/**
 * 削除されたルームに繋がっていた端末に出す。
 *
 * 誰が削除したかは書かない。削除できるのは中央ディスプレイだけだが、この画面は削除を
 * 実行した端末にも、居合わせたプレイヤー端末にも、2台目の中央ディスプレイにも出る。
 */
export function ClosedNotice(): React.ReactElement {
  return (
    <Alert color="orange" title="このルームは削除されました" data-testid="closed-notice">
      {'ルームコードと QR コードは無効になりました。続けて遊ぶには新しい卓を立ててください。'}
    </Alert>
  );
}

export function RejectedNotice({ roomCode }: { roomCode: string }): React.ReactElement {
  return (
    <Alert color="red" title="この卓に接続できません" data-testid="rejected-notice">
      {`ルームコード ${roomCode} の卓が見つかりません。コードを確認するか、卓を立て直してください。ルームは最終更新から24時間で消えます。`}
    </Alert>
  );
}
